import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaPlay, FaPause, FaRedo, FaPlus } from 'react-icons/fa';
import './FocusTimerComponent.css';

const FocusTimerComponent = () => {
  const [time, setTime] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [initialTime, setInitialTime] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [isActive, setIsActive] = useState(false);
  const [selectedMusic, setSelectedMusic] = useState('Chimes');
  const [showAddTimer, setShowAddTimer] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    // Load timer state from localStorage when the component mounts
    const savedTime = JSON.parse(localStorage.getItem('focusTimerTime'));
    const savedInitialTime = JSON.parse(localStorage.getItem('focusTimerInitialTime'));
    const savedIsActive = JSON.parse(localStorage.getItem('focusTimerIsActive'));
    const savedMusic = localStorage.getItem('focusTimerMusic');

    if (savedTime) setTime(savedTime);
    if (savedInitialTime) setInitialTime(savedInitialTime);
    if (savedMusic) setSelectedMusic(savedMusic);

    // If the timer was active before, resume it
    if (savedIsActive) {
      setIsActive(savedIsActive);
      if (savedIsActive) {
        playMusic(); // Resume the music
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Save the timer state to localStorage whenever it changes
    localStorage.setItem('focusTimerTime', JSON.stringify(time));
    localStorage.setItem('focusTimerInitialTime', JSON.stringify(initialTime));
    localStorage.setItem('focusTimerIsActive', JSON.stringify(isActive));
    localStorage.setItem('focusTimerMusic', selectedMusic);
  }, [time, isActive, selectedMusic, initialTime]);

  const playMusic = useCallback(() => {
    const musicPaths = {
      Chimes: '/assets/sounds/chimes.mp3',
      Xylophone: '/assets/sounds/xylophone.mp3',
      Chords: '/assets/sounds/chords.mp3',
      Tap: '/assets/sounds/tap.mp3',
      Jingle: '/assets/sounds/jingle.mp3',
      Transition: '/assets/sounds/transition.mp3',
      Relaxing1: '/assets/sounds/relaxing1.mp3',
      Relaxing2: '/assets/sounds/relaxing2.mp3',
      Relaxing3: '/assets/sounds/relaxing3.mp3',
      Relaxing4: '/assets/sounds/relaxing4.mp3',
      Relaxing5: '/assets/sounds/relaxing5.mp3'
    };

    if (audioRef.current) {
      audioRef.current.pause();
    }

    audioRef.current = new Audio(musicPaths[selectedMusic]);
    audioRef.current.loop = true;
    audioRef.current.play().catch(error => console.error("Failed to play audio:", error));
  }, [selectedMusic]);

  useEffect(() => {
    let interval = null;

    if (isActive) {
      interval = setInterval(() => {
        const { hours, minutes, seconds } = time;

        if (seconds > 0) {
          setTime({ hours, minutes, seconds: seconds - 1 });
        } else if (minutes > 0) {
          setTime({ hours, minutes: minutes - 1, seconds: 59 });
        } else if (hours > 0) {
          setTime({ hours: hours - 1, minutes: 59, seconds: 59 });
        } else {
          clearInterval(interval);
          setIsActive(false);
          if (audioRef.current) {
            audioRef.current.pause(); // Stop the music
          }
          showNotification(); // Show notification
        }
      }, 1000);
    } else {
      clearInterval(interval);
      if (audioRef.current) {
        audioRef.current.pause(); // Pause the music if the timer is paused
      }
    }

    return () => clearInterval(interval);
  }, [isActive, time, playMusic]);

  const startTimer = () => {
    setIsActive(true);
    playMusic(); // Start playing music when the timer starts
  };

  const pauseTimer = () => {
    setIsActive(false);
    if (audioRef.current) {
      audioRef.current.pause(); // Pause the music when the timer is paused
    }
  };

  const resetTimer = () => {
    setIsActive(false);
    setTime(initialTime); // Reset to initial time
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    localStorage.removeItem('focusTimerTime');
  };

  const handleMusicChange = (e) => {
    setSelectedMusic(e.target.value);
  };

  const handleAddTimer = () => {
    setShowAddTimer(true);
  };

  const closeAddTimer = () => {
    setShowAddTimer(false);
  };

  const updateTimer = (field, value) => {
    const updatedTime = {
      ...time,
      [field]: parseInt(value, 10),
    };
    setTime(updatedTime);
    setInitialTime(updatedTime);
    localStorage.setItem('focusTimerTime', JSON.stringify(updatedTime));
    localStorage.setItem('focusTimerInitialTime', JSON.stringify(updatedTime));
  };

  const showNotification = () => {
    new Notification("Focus Timer", {
      body: "Time's up!",
      icon: "/path/to/notification-icon.png" // Optional: add an icon for the notification
    });
  };

  const calculateProgress = (unit, totalUnits) => {
    return (time[unit] / totalUnits) * 100;
  };

  const getCircleStyle = (unit, totalUnits) => {
    const progress = calculateProgress(unit, totalUnits);
    return {
      background: `conic-gradient(
        var(--focus-${unit}-color) ${progress}%, 
        white ${progress}% 100%
      )`,
      borderRadius: '50%',
      boxShadow: `0 0 15px var(--focus-${unit}-color)`,
    };
  };

  return (
    <div className="focus-timer-container">
      <h2>Focus Timer</h2>
      <div className="focus-timer-display">
        <div className="focus-circle focus-hours-circle" style={getCircleStyle('hours', 24)}>
          <div className="focus-time">
            <span>{String(time.hours).padStart(2, '0')}</span>
            <p>HOURS</p>
          </div>
        </div>
        <div className="focus-circle focus-minutes-circle" style={getCircleStyle('minutes', 60)}>
          <div className="focus-time">
            <span>{String(time.minutes).padStart(2, '0')}</span>
            <p>MINUTES</p>
          </div>
        </div>
        <div className="focus-circle focus-seconds-circle" style={getCircleStyle('seconds', 60)}>
          <div className="focus-time">
            <span>{String(time.seconds).padStart(2, '0')}</span>
            <p>SECONDS</p>
          </div>
        </div>
      </div>
      <div className="focus-timer-controls">
        <button onClick={isActive ? pauseTimer : startTimer} className="focus-control-button play-pause">
          {isActive ? <FaPause /> : <FaPlay />}
        </button>
        <button onClick={resetTimer} className="focus-control-button"><FaRedo /></button>
        <button onClick={handleAddTimer} className="focus-control-button"><FaPlus /></button>
      </div>
      {showAddTimer && (
        <div className="focus-add-timer-popup">
          <div className="focus-popup-content">
            <h3>Add Timer</h3>
            <div className="focus-time-settings">
              <div className="focus-time-input">
                <label>Hours</label>
                <input
                  type="number"
                  value={time.hours}
                  onChange={(e) => updateTimer('hours', e.target.value)}
                  min="0"
                />
              </div>
              <div className="focus-time-input">
                <label>Minutes</label>
                <input
                  type="number"
                  value={time.minutes}
                  onChange={(e) => updateTimer('minutes', e.target.value)}
                  min="0"
                />
              </div>
              <div className="focus-time-input">
                <label>Seconds</label>
                <input
                  type="number"
                  value={time.seconds}
                  onChange={(e) => updateTimer('seconds', e.target.value)}
                  min="0"
                />
              </div>
            </div>
            <div className="focus-music-selector">
              <label>Select Music:</label>
              <div className="focus-dropdown-container">
                <select value={selectedMusic} onChange={handleMusicChange}>
                  <option value="Chimes">Chimes</option>
                  <option value="Xylophone">Xylophone</option>
                  <option value="Chords">Chords</option>
                  <option value="Tap">Tap</option>
                  <option value="Jingle">Jingle</option>
                  <option value="Transition">Transition</option>
                  <option value="Relaxing1">Relaxing1</option>
                  <option value="Relaxing2">Relaxing2</option>
                  <option value="Relaxing3">Relaxing3</option>
                  <option value="Relaxing4">Relaxing4</option>
                  <option value="Relaxing5">Relaxing5</option>
                </select>
                  <button className="focus-play-sound" onClick={playMusic}>
                    <span role="img" aria-label="play">▶️</span>
                  </button>

              </div>
            </div>
            <div className="focus-popup-actions">
              <button onClick={closeAddTimer}>Close</button>
              <button onClick={() => { closeAddTimer(); startTimer(); }}>Start Timer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FocusTimerComponent;
